import { useState } from 'react'
import { Link, LoaderFunctionArgs, useLoaderData, useParams, useRevalidator } from 'react-router-dom'
import { useCurrentHealthCenter, useIsAccountClosed } from '../../components/AppShell'
import {
  Accordion,
  Button,
  ButtonLink,
  CurrencyField,
  DateField,
  Modal,
  Panel,
  PanelBody,
  PanelHeading,
  TextAreaField,
  useConfirm,
  useToast,
} from '../../components/common'
import { PatientCompleteInfo } from '../patients/PatientShowPage'
import { fetchPatient } from '../../api/patients'
import {
  ProgramOfTreatment,
  Treatment,
  TreatmentPayment,
  createProgramPayment,
  createTreatmentPayment,
  deleteProgramOfTreatment,
  deleteTreatment,
  fetchTreatments,
} from '../../api/treatments'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { formatMoney, hcCurrency } from '../../lib/currency'
import { printHtml } from '../../lib/print'

const PAID_BACKGROUND = '#C8E6C9'
const UNPAID_BACKGROUND = '#F5F5F5'

type PaymentTarget = { kind: 'treatment' | 'program'; id: number; price: string }

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function MultilineText({ text }: { text: string | null }) {
  return (
    <>
      {(text ?? '').split(/\r?\n/).map((line, index) => (
        <span key={index}>
          {index > 0 && <br />}
          {line}
        </span>
      ))}
    </>
  )
}

function PaymentsTable({ payments }: { payments: readonly TreatmentPayment[] }) {
  return (
    <table className="table table-bordered">
      <thead>
        <tr>
          <th>#</th>
          <th>Fecha</th>
          <th>Monto</th>
          <th>Nota</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((payment, index) => (
          <tr key={payment.id} data-testid={`row-${payment.id}`}>
            <th>{index + 1}</th>
            <th>{payment.date}</th>
            <td>{hcCurrency(payment.price)}</td>
            <td>{payment.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function currencyFigure(amount: number): string {
  return hcCurrency(amount).replace('$', '$ ')
}

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.body !== null && typeof error.body === 'object') {
    const messages = Object.entries(error.body as Record<string, string[]>).map(
      ([field, fieldMessages]) => `${field} ${fieldMessages.join(', ')}`,
    )
    if (messages.length > 0) {
      return messages.join(' · ')
    }
  }
  return 'No pudimos completar la operación. Intenta nuevamente.'
}

function Figure({ size, label, value }: { size: number; label: string; value: string }) {
  return (
    <div className={`col-xs-${size}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function TreatmentPanel({
  treatment,
  onDelete,
  onPay,
}: {
  treatment: Treatment
  onDelete: () => void
  onPay: () => void
}) {
  const closed = useIsAccountClosed()
  return (
    <Accordion
      id={`panel_treatment_${treatment.id}`}
      className="hover-shadow"
      style={{ border: 'thin solid #e9eaed' }}
      headingStyle={{ backgroundColor: treatment.price <= treatment.paid ? PAID_BACKGROUND : UNPAID_BACKGROUND }}
      heading={
        <>
          <div className="col-xs-2">{treatment.date}</div>
          <div className="col-xs-4">{treatment.name}</div>
          <div className="col-xs-2">{treatment.code}</div>
          <div className="col-xs-3">{hcCurrency(treatment.price)}</div>
          <div className="col-xs-1">{treatment.timeless ? '-' : (treatment.fee ?? '-')}</div>
        </>
      }
    >
      <div className="row">
        <div className="col-xs-12 text-right">
          <a
            className="card-link"
            onClick={() => {
              trackEvent('Treatment Printed', { kind: 'treatment' })
              printHtml(treatment.printable_html ?? '')
            }}
          >
            Imprimir
          </a>
          {!closed && treatment.deletable && (
            <>
              {'|'}
              <a className="card-link" onClick={onDelete}>
                Eliminar
              </a>
            </>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <dl>
            <dt>Descripción</dt>
            <dd>
              <MultilineText text={treatment.description} />
            </dd>
          </dl>
          <dl>
            <dt>Nota</dt>
            <dd>
              <MultilineText text={treatment.note} />
            </dd>
          </dl>
          {treatment.timeless && (
            <dl>
              <dt>Tipo de Duración</dt>
              <dd>Indefinida</dd>
            </dl>
          )}
          <div className="row text-center">
            {treatment.timeless ? (
              <>
                <Figure size={4} label="Precio de cada sesión" value={currencyFigure(treatment.price)} />
                <Figure size={4} label="Sesiones pagadas" value={String(treatment.payments.length)} />
                <Figure size={4} label="Total Pagado" value={currencyFigure(treatment.paid)} />
              </>
            ) : (
              <>
                <Figure size={3} label="Total" value={currencyFigure(treatment.price)} />
                <Figure size={3} label="Cuotas totales" value={String(treatment.fee ?? '')} />
                <Figure size={3} label="Pagado" value={currencyFigure(treatment.paid)} />
                <Figure size={3} label="Cuotas pagadas" value={String(treatment.payments.length)} />
              </>
            )}
          </div>
          <br />
          <PaymentsTable payments={treatment.payments} />
          {!closed && (
            <div className="text-right">
              <Button style={{ borderRadius: '50%' }} onClick={onPay}>
                <i className="fa fa-plus" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </Accordion>
  )
}

function ProgramPanel({
  program,
  onDelete,
  onPay,
}: {
  program: ProgramOfTreatment
  onDelete: () => void
  onPay: () => void
}) {
  const closed = useIsAccountClosed()
  const difference = program.price - program.paid

  return (
    <Accordion
      id={`panel_program_of_treatment_${program.id}`}
      className="hover-shadow"
      style={{ border: 'thin solid #e9eaed' }}
      headingStyle={{ backgroundColor: program.price <= program.paid ? PAID_BACKGROUND : UNPAID_BACKGROUND }}
      heading={
        <>
          <div className="col-xs-2">{program.date}</div>
          <div className="col-xs-4">{program.description}</div>
          <div className="col-xs-2">{program.name}</div>
          <div className="col-xs-3">{hcCurrency(program.price)}</div>
          <div className="col-xs-1">{program.payments.length}</div>
        </>
      }
    >
      <div className="row">
        <div className="col-xs-12 text-right">
          <a
            className="card-link"
            onClick={() => {
              trackEvent('Treatment Printed', { kind: 'program' })
              printHtml(program.printable_html ?? '')
            }}
          >
            Imprimir
          </a>
          {!closed && program.deletable && (
            <>
              {'|'}
              <a className="card-link" onClick={onDelete}>
                Eliminar
              </a>
            </>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12">
          <div className="row">
            <h4 className="text-center">Detalle de tratamientos y sus precios</h4>
            <div className="col-xs-12">
              <ul className="text-left">
                {program.treatments.map((line, index) => (
                  <li key={index}>{`${line.name}: $${line.price} ${line.timeless ? 'por sesión' : 'total'} (${line.note})`}</li>
                ))}
              </ul>
            </div>
            <div className="col-xs-12">
              <dl>
                <dt>Profesional</dt>
                <dd>
                  <MultilineText text={program.name} />
                </dd>
              </dl>
            </div>
            <div className="col-xs-12">
              <dl>
                <dt>Descripción</dt>
                <dd>
                  <MultilineText text={program.description} />
                </dd>
              </dl>
            </div>
            <div className="col-xs-12 text-center">
              <Figure size={4} label="Total a Pagar" value={currencyFigure(program.price)} />
              <Figure size={4} label="Total Pagado" value={currencyFigure(program.paid)} />
              <Figure size={4} label="Diferencia" value={currencyFigure(difference < 0 ? 0 : difference)} />
            </div>
            <div className="col-xs-12">
              <PaymentsTable payments={program.payments} />
            </div>
            {!closed && (
              <div className="text-right col-xs-12">
                <Button style={{ borderRadius: '50%' }} onClick={onPay}>
                  <i className="fa fa-plus" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Accordion>
  )
}

function PaymentModal({
  target,
  onClose,
  onSubmit,
}: {
  target: PaymentTarget
  onClose: () => void
  onSubmit: (payment: { date: string; price: string; note: string }) => void
}) {
  const [date, setDate] = useState(today())
  const [price, setPrice] = useState(target.price)
  const [note, setNote] = useState('')

  return (
    <Modal
      open
      onClose={onClose}
      title={target.kind === 'treatment' ? 'Nuevo Pago' : 'Nuevo Pago 2'}
      footer={<Button onClick={() => onSubmit({ date, price, note })}>Pagar</Button>}
    >
      <DateField label="Fecha" labelStyle="plain" value={date} onChange={setDate} />
      <CurrencyField label="Monto a pagar" value={price} onChange={setPrice} />
      <TextAreaField label="Nota" labelStyle="plain" rows={2} value={note} onChange={setNote} />
    </Modal>
  )
}

export async function loader({ params }: LoaderFunctionArgs) {
  const healthCenterId = Number(params.healthCenterId)
  const patientId = params.patientId as string
  const [data, patient] = await Promise.all([fetchTreatments(healthCenterId, patientId), fetchPatient(healthCenterId, patientId)])
  return { data, patient }
}

export default function TreatmentsIndexPage() {
  const healthCenter = useCurrentHealthCenter()
  const closed = useIsAccountClosed()
  const params = useParams()
  const confirm = useConfirm()
  const showToast = useToast()
  const patientId = params.patientId ?? ''
  const { data, patient } = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const { revalidate } = useRevalidator()
  const [paymentTarget, setPaymentTarget] = useState<PaymentTarget | null>(null)

  const openPaymentModal = (target: PaymentTarget) => {
    trackEvent('Treatment Payment Modal Opened', { kind: target.kind })
    setPaymentTarget(target)
  }

  const dismissPaymentModal = () => {
    if (paymentTarget) {
      trackEvent('Treatment Payment Modal Dismissed', { kind: paymentTarget.kind })
    }
    setPaymentTarget(null)
  }

  if (!healthCenter) {
    return null
  }

  const askAndDelete = async (event: 'Treatment Deleted' | 'Program Of Treatment Deleted', remove: () => Promise<void>) => {
    if (await confirm({ title: 'Eliminar', message: 'Estas seguro?' })) {
      try {
        await remove()
      } catch (error) {
        showToast('error', getErrorMessage(error))
        return
      }
      trackEvent(event)
      void revalidate()
    }
  }

  const submitPayment = async (payment: { date: string; price: string; note: string }) => {
    if (!paymentTarget) {
      return
    }
    try {
      if (paymentTarget.kind === 'treatment') {
        await createTreatmentPayment(healthCenter.id, patientId, paymentTarget.id, payment)
      } else {
        await createProgramPayment(healthCenter.id, patientId, paymentTarget.id, payment)
      }
    } catch (error) {
      trackEvent('Treatment Payment Failed', {
        reason: getErrorMessage(error),
        status_code: error instanceof ApiError ? error.status : undefined,
      })
      showToast('error', getErrorMessage(error))
      return
    }
    trackEvent('Treatment Payment Recorded', { kind: paymentTarget.kind, amount: payment.price })
    setPaymentTarget(null)
    void revalidate()
  }

  return (
    <div className="col-xs-12 col-md-8 col-md-offset-2" style={{ backgroundColor: 'white', paddingBottom: 10, marginTop: 15 }}>
      <PanelHeading className="text-center">
        <h1>Lista de Tratamientos de {data.patient.name}</h1>
      </PanelHeading>
      <PanelBody>
        <Panel>
          <div className="module-wrapper col-md-12 col-sm-12 col-xs-12 view health-view">
            <PatientCompleteInfo patient={patient} healthCenterId={healthCenter.id} />
          </div>
        </Panel>

        {data.treatments.length > 0 && (
          <>
            <br />
            <div className="col-xs-12 text-center">
              <h3>Tratamientos</h3>
            </div>
            <br />
            <div className="row">
              <h5>
                <div className="col-xs-2">Fecha</div>
              </h5>
              <h5>
                <div className="col-xs-4">Nombre</div>
              </h5>
              <h5>
                <div className="col-xs-2">Codigo</div>
              </h5>
              <h5>
                <div className="col-xs-3">Precio</div>
              </h5>
              <h5>
                <div className="col-xs-1">Nº Cuotas</div>
              </h5>
            </div>
            <br />
          </>
        )}
        <div className="panel-group">
          {data.treatments.map((treatment) => (
            <TreatmentPanel
              key={treatment.id}
              treatment={treatment}
              onDelete={() => void askAndDelete('Treatment Deleted', () => deleteTreatment(healthCenter.id, patientId, treatment.id))}
              onPay={() => openPaymentModal({ kind: 'treatment', id: treatment.id, price: formatMoney(String(treatment.price_per_fee)) })}
            />
          ))}
        </div>
        <br />

        {data.program_of_treatments.length > 0 && (
          <>
            <br />
            <div className="col-xs-12 text-center">
              <h3>Presupuestos</h3>
            </div>
            <br />
            <div className="row">
              <h5>
                <div className="col-xs-2">Fecha</div>
              </h5>
              <h5>
                <div className="col-xs-4">Descripción</div>
              </h5>
              <h5>
                <div className="col-xs-2">Profesional</div>
              </h5>
              <h5>
                <div className="col-xs-3">Precio</div>
              </h5>
              <h5>
                <div className="col-xs-1">Nº Abonos</div>
              </h5>
            </div>
            <br />
          </>
        )}
        <div className="panel-group">
          {data.program_of_treatments.map((program) => (
            <ProgramPanel
              key={program.id}
              program={program}
              onDelete={() => void askAndDelete('Program Of Treatment Deleted', () => deleteProgramOfTreatment(healthCenter.id, patientId, program.id))}
              onPay={() => openPaymentModal({ kind: 'program', id: program.id, price: '' })}
            />
          ))}
        </div>
        <br />
      </PanelBody>

      {paymentTarget && (
        <PaymentModal target={paymentTarget} onClose={dismissPaymentModal} onSubmit={(payment) => void submitPayment(payment)} />
      )}

      {!closed && (
        <>
          <ButtonLink to={`/health_centers/${healthCenter.id}/patients/${patientId}/treatments/new`}>
            Nuevo Tratamiento Unico
          </ButtonLink>{' '}
          <ButtonLink to={`/health_centers/${healthCenter.id}/patients/${patientId}/program_of_treatments/new`}>
            Nuevo Programa de Tratamientos
          </ButtonLink>
        </>
      )}
    </div>
  )
}
